import React from 'react';

/* A load test's phases on a time chart: a strip above the plot with one segment per span,
   labelled as far as it has room, and a faint band down the plot behind the series, so a
   point is read against its phase without a label in the way. A phase's segment is split
   where its sending ended: the stronger part while users were still being requested
   (phase 1) or messages sent, the lighter part the wait for the last agents or answers.
   The hold and the cleanup are grey.

   They replaced labelled vertical bars (Mark, 2026-10-18): a short run left no room for
   the labels, so a chart could show eight bars and six labels, and "Phase 3: all sent"
   was long to read. Here every span keeps a label, shortened to its number when narrow.

     timeline: [{ kind: 'phase'|'hold'|'cleanup', n?, label, start, sent?, end?,
                  during?, after? }]                     epoch ms, the report's `timeline`
     phasesOn(rows, timeline) -> [{ ...span, from, sent, to }]        in row units

   `during` and `after` name the two parts in the tooltip ("sending", "waiting for
   answers"). Rows carry their time as `t`: epoch ms (the report's own rows) or an ISO
   string (the Platform's). A span is clipped to the rows; one outside them is dropped. */

export const RIBBON_H = 16;   // the strip's height
export const RIBBON_GAP = 6;  // between the strip and the plot
const RIBBON_FONT = 10.5;
const CHAR_W = 6.1;           // average advance at RIBBON_FONT, semibold
const PAD_X = 6;              // a label's inset from its segment's start

const ms = v => (typeof v === 'number' ? v : new Date(v).getTime());

export function phasesOn(rows, timeline, key = 't') {
  if (!rows?.length || !timeline?.length) return [];
  const t0 = ms(rows[0][key]);
  const step = rows.length > 1 ? ms(rows[1][key]) - t0 : 60000;
  if (!Number.isFinite(t0) || !(step > 0)) return [];
  const last = rows.length - 1;
  const pos = t => (t - t0) / step;
  const clip = p => Math.max(0, Math.min(last, p));
  return timeline.map((s) => {
    if (!Number.isFinite(s?.start)) return null;
    const a = pos(s.start);
    // A span still running reaches the last point.
    const b = Number.isFinite(s.end) ? pos(s.end) : last;
    if (a > last || b < 0) return null;
    const from = clip(a);
    const to = clip(b);
    if (!(to > from) && rows.length > 1) return null;
    const sent = Number.isFinite(s.sent) ? pos(s.sent) : null;
    return { ...s, from, to, sent: sent !== null && sent > from && sent < to ? sent : null };
  }).filter(Boolean);
}

/* The spans in pixels on a chart that puts row `i` at xOf(i). */
export function phaseSegments(phases, xOf) {
  return (phases || []).map(p => ({
    ...p, x0: xOf(p.from), x1: xOf(p.to), xs: p.sent === null || p.sent === undefined ? null : xOf(p.sent),
  }));
}

/* The span a row falls in: the one that starts last, so a point on a boundary belongs to
   the phase that begins there. */
export function phaseAt(phases, index) {
  let hit = null;
  (phases || []).forEach((p) => {
    if (index >= p.from - 1e-9 && index <= p.to + 1e-9 && (!hit || p.from >= hit.from)) hit = p;
  });
  return hit;
}

/* "Phase 2 · Messages, waiting for answers": what the tooltip says about a row. */
export function phaseText(p, index) {
  if (!p) return '';
  const name = p.kind === 'phase' ? `Phase ${p.n} · ${p.label}` : p.label;
  if (p.sent === null || p.sent === undefined) return name;
  const part = index < p.sent ? p.during : p.after;
  return part ? `${name}, ${part}` : name;
}

// The colours are theme tokens (styles/tokens.css --phase-*): one opacity cannot read on
// both a white and a near-black card.
const TONE = {
  phase: { strong: 'var(--phase-strong)', weak: 'var(--phase-weak)', text: 'var(--accent-text)',
           band: 'var(--phase-band)', bandAfter: 'var(--phase-band-after)', edge: 'var(--phase-edge)' },
  hold: { strong: 'var(--phase-neutral)', weak: 'var(--phase-neutral)', text: 'var(--text-2)',
          band: null, bandAfter: null, edge: null },
};
const toneOf = kind => TONE[kind] || TONE.hold;

// A bar with either end rounded, so the split between a phase's two parts stays square.
function bar(x0, x1, y, h, r, left, right) {
  const w = Math.max(0, x1 - x0);
  const rl = left ? Math.min(r, w / 2, h / 2) : 0;
  const rr = right ? Math.min(r, w / 2, h / 2) : 0;
  return `M${x0 + rl},${y}H${x1 - rr}`
    + (rr ? `A${rr},${rr} 0 0 1 ${x1},${y + rr}V${y + h - rr}A${rr},${rr} 0 0 1 ${x1 - rr},${y + h}` : `V${y + h}`)
    + `H${x0 + rl}`
    + (rl ? `A${rl},${rl} 0 0 1 ${x0},${y + h - rl}V${y + rl}A${rl},${rl} 0 0 1 ${x0 + rl},${y}` : `V${y}`)
    + 'Z';
}

// The longest label that fits: "1 Onboarding", then "1"; the hold and the cleanup by name.
function fitLabel(s, width, charW) {
  const room = width - PAD_X * 2;
  const options = s.kind === 'phase'
    ? [[String(s.n), s.label || null], [String(s.n), null]]
    : [[null, s.label || null]];
  return options.find(([num, name]) => (num || name)
    && `${num || ''}${num && name ? ' ' : ''}${name || ''}`.length * charW <= room) || null;
}

/* The bands down the plot, behind the series, and a hairline where each phase starts. */
export function PhaseShading({ segments, top, bottom }) {
  if (!segments?.length) return null;
  const h = Math.max(0, bottom - top);
  return (
    <g aria-hidden="true">
      {segments.map((s) => {
        const t = toneOf(s.kind);
        if (!t.band) return null;
        const split = s.xs ?? s.x1;
        return (
          <g key={`band-${s.kind}-${s.n ?? ''}-${s.from}`}>
            <rect x={s.x0} y={top} width={Math.max(0, split - s.x0)} height={h} fill={t.band} />
            {s.xs !== null && s.xs !== undefined && (
              <rect x={s.xs} y={top} width={Math.max(0, s.x1 - s.xs)} height={h} fill={t.bandAfter} />
            )}
            <line x1={s.x0} y1={top} x2={s.x0} y2={bottom} stroke={t.edge} strokeWidth={1} />
          </g>
        );
      })}
    </g>
  );
}

/* The strip: a segment per span from `y`, `h` tall, its label inside as far as it fits.
   `title(segment)`, when given, is each segment's hover text: for a strip that is not on
   a chart, whose tooltip would say it. */
export function PhaseRibbon({ segments, y, h = RIBBON_H, fontSize = RIBBON_FONT, title }) {
  if (!segments?.length) return null;
  const charW = CHAR_W * (fontSize / RIBBON_FONT);
  return (
    <g aria-hidden="true">
      {segments.map((s) => {
        const t = toneOf(s.kind);
        // A hairline gap between neighbours, so back-to-back phases read as two.
        const x0 = s.x0 + 1;
        const x1 = Math.max(x0 + 2, s.x1 - 1);
        const xs = s.xs === null || s.xs === undefined ? null : Math.min(Math.max(s.xs, x0), x1);
        const label = fitLabel(s, x1 - x0, charW);
        return (
          <g key={`seg-${s.kind}-${s.n ?? ''}-${s.from}`}>
            {title && <title>{title(s)}</title>}
            {xs === null ? (
              <path d={bar(x0, x1, y, h, 4, true, true)} fill={t.strong} />
            ) : (
              <>
                <path d={bar(x0, xs, y, h, 4, true, false)} fill={t.strong} />
                <path d={bar(xs, x1, y, h, 4, false, true)} fill={t.weak} />
              </>
            )}
            {label && (
              <text x={x0 + PAD_X} y={y + h / 2} dominantBaseline="central" fontSize={fontSize}
                    fill={t.text}>
                {label[0] && <tspan fontWeight={700}>{label[0]}</tspan>}
                {label[1] && <tspan fontWeight={600} dx={label[0] ? fontSize * 0.36 : 0}>{label[1]}</tspan>}
              </text>
            )}
          </g>
        );
      })}
    </g>
  );
}
